import React, { useState, useEffect } from "react";
import { X, LinkSimple, Copy, CheckCircle, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { logError } from "@/lib/logger";

/**
 * Generates a candidate self-booking link (`/book/{token}`) and lets the
 * recruiter copy it or email it to the candidate. Ships in V1.3 as the
 * MVP of the Cal.com-style self-scheduling flow.
 */
export default function SendBookingLinkModal({ candidateId, candidate, onClose }) {
    const [duration, setDuration] = useState(30);
    const [days, setDays] = useState(7);
    const [creating, setCreating] = useState(false);
    const [link, setLink] = useState(null);
    const [copied, setCopied] = useState(false);
    const [sending, setSending] = useState(false);
    const [gcalStatus, setGcalStatus] = useState(null);

    // V1.3 (Feb 2026) — Fetch the recruiter's Google Calendar status so we
    // can show a "Reconnect Google" nudge when their pre-Feb-2026 tokens
    // lack the `calendar.readonly` scope needed for freeBusy filtering.
    useEffect(() => {
        api.get("/auth/google/status").then((r) => setGcalStatus(r.data)).catch(() => {});
    }, []);

    const connectGoogle = async () => {
        try {
            const r = await api.get("/auth/google/connect");
            if (r.data?.authorization_url) window.location.href = r.data.authorization_url;
        } catch (ex) {
            toast.error("Could not start Google connect flow.");
        }
    };

    const create = async () => {
        if (creating) return;
        setCreating(true);
        try {
            const r = await api.post(`/candidates/${candidateId}/booking-link`, {
                duration_minutes: duration,
                days_ahead: days,
            });
            setLink(r.data);
            toast.success("Booking link ready");
        } catch (ex) {
            logError("booking-link-create-failed", ex);
            toast.error(ex?.response?.data?.detail || "Could not create booking link.");
        } finally {
            setCreating(false);
        }
    };

    const copy = async () => {
        if (!link?.url) return;
        try {
            await navigator.clipboard.writeText(link.url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2200);
        } catch {
            toast.error("Copy failed — long-press the URL to copy manually.");
        }
    };

    const emailToCandidate = async () => {
        if (!link?.url || sending) return;
        setSending(true);
        try {
            const subject = "Book your interview slot";
            const body = `<p>Hi ${candidate?.name?.split(" ")[0] || "there"},</p>
<p>Please pick a convenient time for your interview using the link below. The link expires in 10 days.</p>
<p><a href="${link.url}" style="display:inline-block;background:#0A0A0A;color:#FFF;padding:12px 22px;text-decoration:none;font-weight:700;letter-spacing:.08em;text-transform:uppercase;font-size:12px">Pick a slot →</a></p>
<p style="color:#525252;font-size:12px">Or paste this URL into your browser: <br>${link.url}</p>`;
            await api.post(`/candidates/${candidateId}/email`, {
                subject,
                body,
                eyebrow: "INTERVIEW",
                headline: "Pick a time that works",
                cta_label: "Pick a slot",
                cta_url: link.url,
            });
            toast.success("Booking link emailed to candidate");
            onClose?.();
        } catch (ex) {
            logError("booking-link-email-failed", ex);
            toast.error(ex?.response?.data?.detail || "Could not send email.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            data-testid="send-booking-link-modal"
            className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white max-w-lg w-full border border-[#E5E5E5]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[#E5E5E5] px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="inline-flex items-center justify-center w-9 h-9 border border-cyan-200 bg-cyan-50 text-cyan-700">
                            <LinkSimple size={16} weight="bold" />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#A3A3A3]">
                                Self-booking
                            </p>
                            <h2 className="font-heading text-lg font-black text-ink">Send booking link</h2>
                        </div>
                    </div>
                    <button data-testid="booking-modal-close" onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]">
                        <X size={16} weight="bold" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    {!link ? (
                        <>
                            <p className="text-sm text-[#525252]">
                                Generate a shareable link that lets {candidate?.name?.split(" ")[0] || "the candidate"} pick a slot from your available hours. Link expires in 10 days.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-[#525252]">Duration</label>
                                    <select
                                        data-testid="booking-duration"
                                        value={duration}
                                        onChange={(e) => setDuration(parseInt(e.target.value, 10))}
                                        className="w-full mt-1 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                                    >
                                        <option value={15}>15 min</option>
                                        <option value={30}>30 min</option>
                                        <option value={45}>45 min</option>
                                        <option value={60}>60 min</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-[#525252]">Days ahead</label>
                                    <select
                                        data-testid="booking-days"
                                        value={days}
                                        onChange={(e) => setDays(parseInt(e.target.value, 10))}
                                        className="w-full mt-1 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                                    >
                                        <option value={3}>Next 3 days</option>
                                        <option value={7}>Next 7 days</option>
                                        <option value={14}>Next 14 days</option>
                                    </select>
                                </div>
                            </div>
                            <button
                                data-testid="booking-create-btn"
                                onClick={create}
                                disabled={creating}
                                className="btn-gradient w-full py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-40"
                            >
                                {creating ? "Creating…" : "Generate link"}
                            </button>
                            {/* Optional Google Calendar upsell — small, inline, dismissible.
                                The .ics attachment already lands in every candidate's calendar
                                without setup; Google unlocks auto-Meet-links + free-slot filter. */}
                            <GoogleCalendarUpsell status={gcalStatus} onConnect={connectGoogle} />
                        </>
                    ) : (
                        <>
                            <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 p-3">
                                <CheckCircle size={16} weight="bold" className="text-emerald-700 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <p className="text-sm font-bold text-emerald-900">Link ready</p>
                                    <p className="text-xs text-emerald-800 mt-0.5">Copy it or email it to the candidate.</p>
                                </div>
                            </div>
                            <div className="flex items-stretch gap-1 border border-[#E5E5E5]">
                                <input
                                    data-testid="booking-link-url"
                                    readOnly
                                    value={link.url}
                                    onClick={(e) => e.target.select()}
                                    className="flex-1 px-3 py-2 text-xs bg-[#FAFAFA] font-mono text-ink truncate"
                                />
                                <button
                                    data-testid="booking-copy-btn"
                                    onClick={copy}
                                    className="px-3 border-l border-[#E5E5E5] hover:bg-[#F5F5F5] text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1"
                                >
                                    {copied ? <><CheckCircle size={12} weight="bold" /> Copied</> : <><Copy size={12} weight="bold" /> Copy</>}
                                </button>
                            </div>
                            <button
                                data-testid="booking-email-btn"
                                onClick={emailToCandidate}
                                disabled={sending || !candidate?.email}
                                className="btn-gradient w-full py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40"
                            >
                                <Envelope size={14} weight="bold" />
                                {sending ? "Sending…" : `Email to ${candidate?.email || "candidate"}`}
                            </button>
                            {!candidate?.email && (
                                <p className="text-xs text-rose-700">This candidate has no email on file — please add one or copy the link manually.</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/**
 * Tiny inline chip that appears under the "Generate link" CTA.
 *
 * Design intent (Feb 2026): the .ics email attachment is the DEFAULT and works
 * for every recruiter without setup — every candidate gets an "Add to
 * Calendar" file that lands natively in Gmail/Outlook/Apple Mail. Google
 * Calendar sync is an OPTIONAL upgrade that unlocks:
 *   1. Auto free-slot filtering (never offer a time you're already busy)
 *   2. A native Google Meet link on booking
 *
 * We surface it as a small, dismissible chip below the primary action so it
 * feels like a nice-to-have, not a launch-blocker.
 */
function GoogleCalendarUpsell({ status, onConnect }) {
    const [dismissed, setDismissed] = React.useState(() => {
        try { return localStorage.getItem("gcal_upsell_dismissed") === "1"; } catch { return false; }
    });
    if (!status || !status.configured) return null;
    // Nothing to nudge if the recruiter is already fully connected with the
    // right scopes — we're all set.
    if (status.connected && status.has_readonly_scope) return null;
    if (dismissed) return null;

    const isReconnect = status.needs_reconnect;   // has old scopes, missing readonly
    const label = isReconnect ? "Reconnect Google Calendar" : "Enable Google Calendar sync";
    const help = isReconnect
        ? "Your Google connection is older — reconnecting once unlocks auto free-slot filtering."
        : "Optional. Auto-hides slots when you're busy and adds a Meet link to bookings.";

    return (
        <div
            data-testid="gcal-upsell"
            className="flex items-start gap-2 border border-[#E5E5E5] bg-[#FAFAFA] px-3 py-2"
        >
            <LinkSimple size={14} weight="bold" className="text-cobalt shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <p className="text-[11px] font-bold text-ink">
                    {label}
                    <span className="ml-1.5 font-normal text-[10px] text-[#A3A3A3] uppercase tracking-wider">Optional</span>
                </p>
                <p className="text-[10px] text-[#525252] mt-0.5">{help}</p>
                <div className="flex items-center gap-2 mt-1.5">
                    <button
                        type="button"
                        data-testid={isReconnect ? "gcal-reconnect-btn" : "gcal-connect-btn"}
                        onClick={onConnect}
                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-ink"
                    >
                        {isReconnect ? "Reconnect →" : "Connect →"}
                    </button>
                    <span className="text-[10px] text-[#D4D4D4]">·</span>
                    <button
                        type="button"
                        data-testid="gcal-upsell-dismiss"
                        onClick={() => {
                            try { localStorage.setItem("gcal_upsell_dismissed", "1"); } catch {}
                            setDismissed(true);
                        }}
                        className="text-[10px] text-[#A3A3A3] hover:text-[#525252]"
                    >
                        Not now
                    </button>
                </div>
            </div>
        </div>
    );
}
